import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback } from 'react';
import * as Avatar from '@radix-ui/react-avatar';
import { useAuth, useAuthActions, AdminPortal } from '@frontegg/react';
import { Moon, Sun, ChevronRight, Home, Bot, Database, BarChart3, Wrench, CheckCircle2, Undo2, Code, BookOpen, MessageSquare, Settings, Users, Shield, Boxes, Ruler, AlertTriangle, Lightbulb, Calculator, Hammer, Layers, MessageCircleQuestion } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@/contexts/ThemeContext';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useFeatureFlag } from '@/services/featureFlag.service';
import { RequireRole } from '@/components/RequireRole';
import { BuildAllModal } from '@/components/BuildAllModal';
import { PublishChangesDialog } from '@/components/PublishChangesDialog';
import { useIsInDraftMode, useDraftModeToggle } from '@/hooks/useDraftMode';
import ApiService from '@/services/api.service';
import AuthService from '@/services/auth.service';
import BackgroundService from '@/services/bg.service';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import logoLargeBlack from '../assets/images/pliable/logo_with_text_black.png';
import logoLargeWhite from '../assets/images/pliable/logo_with_text_white.png';
import TenantSwitcher from './TenantSwitcher';

interface MenuItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  onClick?: () => void;
}

// Fires a callback on mount (avoids calling setState during render)
function ImmediateEffect({ fn }: { fn: () => void }) {
  useEffect(() => { fn(); }, []);
  return null;
}

export default function AppSidebar() {
  const { user, isAuthenticated } = useAuth();
  const { logout } = useAuthActions();
  const navigate = useNavigate();
  const authService = AuthService.getInstance();

  // Logout flow: render hidden iframe to force SSO IdP logout,
  // then call Frontegg logout and redirect.
  const [loggingOut, setLoggingOut] = useState(false);

  const finishLogout = useCallback(() => {
    sessionStorage.removeItem('plb_auth_state');
    document.cookie = 'plb_sso_attempted=; Max-Age=0; path=/';

    if (authService.isCustomDomain) {
      // Custom domain cookies live on the main domain — hit clear-custom
      // to clear them, then redirect back to the custom domain root
      // which will trigger sso-bounce for re-auth.
      // Fire-and-forget logout since we're navigating away.
      logout();
      const returnTo = encodeURIComponent(window.location.origin);
      window.location.href = `${authService.authAppUrl}/api/v1/env/clear-custom?returnTo=${returnTo}`;
    } else {
      // On main domain, navigate to /do/logout which handles Frontegg
      // logout properly and clears cookies before redirecting.
      window.location.href = '/do/logout';
    }
  }, [logout]);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { data: tenantConfig, dataUpdatedAt: tcUpdatedAt } = useTenantConfig();
  const isInDraftMode = useIsInDraftMode();
  const onToggleDraftMode = useDraftModeToggle();
  const queryClient = useQueryClient();
  const isTemplateManagementEnabled = useFeatureFlag('template-management', false);
  const isAiAssistantEnabled = useFeatureFlag('ai-assistant', false);
  const [reverting, setReverting] = useState(false);
  const [buildAllOpen, setBuildAllOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  // Track expanded sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const isAdmin = useMemo(() => {
    if (!user) return false;
    const roles = user.roles || [];
    return roles.some(
      (r: any) => (typeof r === 'string' ? r : r?.key || r?.name || '').toLowerCase() === 'admin'
    );
  }, [user]);

  const isAdminOrBuilder = useMemo(() => {
    if (!user) return false;
    const roles = user.roles || [];
    return roles.some((r: any) => {
      const key = (typeof r === 'string' ? r : r?.key || r?.name || '').toLowerCase();
      return key === 'admin' || key === 'builder';
    });
  }, [user]);

  const canViewDataManagement = useMemo(() => {
    if (!user) return false;
    const roles = user.roles || [];
    // Show for Admin, Builder, or ReadOnly. Hide only for ReadOnly_Partner
    // (when that's their highest role). Higher roles take precedence.
    return roles.some((r: any) => {
      const key = (typeof r === 'string' ? r : r?.key || r?.name || '').toLowerCase();
      return key === 'admin' || key === 'builder' || key === 'readonly';
    });
  }, [user]);

  console.log('[Sidebar] ai-assistant feature flag:', isAiAssistantEnabled);

  // Top navigation items
  const topItems: MenuItem[] = [
    { label: 'Home', path: '/', icon: <Home className="h-4 w-4" /> },
    ...(isAiAssistantEnabled ? [{ label: 'Ellie', path: '/ellie', icon: <MessageSquare className="h-4 w-4" /> }] : []),
  ];

  // Main section items
  const mainItems: MenuItem[] = [
    ...(canViewDataManagement ? [{ label: 'Agents', path: '/agents', icon: <Bot className="h-4 w-4" /> }] : []),
    { label: 'Analytics', path: '/analytics', icon: <BarChart3 className="h-4 w-4" /> },
    { label: 'Semantic Layer', path: '/semantic-layer', icon: <BookOpen className="h-4 w-4" /> },
    ...(canViewDataManagement ? [{ label: 'Data Sources', path: '/sources', icon: <Database className="h-4 w-4" /> }] : []),
    ...(canViewDataManagement ? [{ label: 'Context', path: '/data/context', icon: <Lightbulb className="h-4 w-4" /> }] : []),
  ];

  // Develop mode items
  const developItems: MenuItem[] = [
    { label: 'Ruthie', path: '/ruthie', icon: <MessageSquare className="h-4 w-4" /> },
    { label: 'Modeling', path: '/develop/modeling', icon: <Code className="h-4 w-4" /> },
    { label: 'Entities', path: '/develop/entities', icon: <Boxes className="h-4 w-4" /> },
    { label: 'Metrics', path: '/develop/metrics', icon: <Calculator className="h-4 w-4" /> },
    { label: 'Dimensions', path: '/develop/dimensions', icon: <Ruler className="h-4 w-4" /> },
    ...(isTemplateManagementEnabled && isAdmin ? [{ label: 'Templates', path: '/templates/install', icon: <Layers className="h-4 w-4" /> }] : []),
    { label: 'Danger Zone', path: '/develop/danger-zone', icon: <AlertTriangle className="h-4 w-4" /> },
  ];

  const showAdminPortal = () => {
    AdminPortal.show();
  };

  const toggleThenaWidget = () => {
    (document.getElementById('thena-iframe-element') as HTMLIFrameElement | null)?.contentDocument?.dispatchEvent(
      new CustomEvent('TOGGLE_THENA_WIDGET')
    );
  };

  // Bottom items (pinned above user menu)
  const bottomItems: MenuItem[] = [
    { label: 'Get Help', path: '#help', icon: <MessageCircleQuestion className="h-4 w-4" />, onClick: toggleThenaWidget },
    ...(isAdmin ? [
      { label: 'Settings', path: '/settings', icon: <Settings className="h-4 w-4" /> },
      { label: 'User Permissions', path: '/users', icon: <Shield className="h-4 w-4" /> },
      { label: 'User Management', path: '#', icon: <Users className="h-4 w-4" />, onClick: showAdminPortal },
    ] : []),
  ];

  // Auto-expand sections if we're on their routes
  useEffect(() => {
    const allItems = [...topItems, ...mainItems, ...developItems, ...bottomItems];
    const newExpanded = new Set<string>();
    allItems.forEach((item) => {
      if (item.children && location.pathname.startsWith(item.path)) {
        newExpanded.add(item.path);
      }
    });
    setExpandedSections(newExpanded);
  }, [location.pathname]);

  const onToggleExpand = (path: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const handlePublishChanges = () => {
    setPublishOpen(true);
  };

  const handleRevertChanges = async () => {
    if (!confirm('Are you sure you want to revert your changes? We will copy everything in production into your develop mode.')) {
      return;
    }

    setReverting(true);
    try {
      const resp = await ApiService.getInstance().request('POST', '/draftversions/undo') as { job_id: string };
      await BackgroundService.getInstance().waitForJob(resp.job_id);
      queryClient.invalidateQueries();
    } catch (error) {
      console.error('Failed to revert changes:', error);
    } finally {
      setReverting(false);
    }
  };

  const handleToggleDevelopMode = async () => {
    await onToggleDraftMode('sidebar');
  };

  const handleBuildAll = () => {
    setBuildAllOpen(true);
  };

  // Only show sidebar if authenticated
  if (!isAuthenticated || !user) {
    return null;
  }

  const userDisplayName = user.name || user.email || 'User';
  const userEmail = user.email || '';
  const userAvatar = user.profilePictureUrl || undefined;

  // Determine logo to use - check __PLB_ENV__ first for zero-flicker on custom domains,
  // then fall back to tenantConfig from the API, then theme-based default.
  const logoToUse = useMemo(() => {
    const wl = tenantConfig?.whitelabeling;
    if (wl?.enabled !== false && wl?.logo_path) {
      console.log('[Sidebar] logo from tenantConfig:', wl.logo_path);
      return wl.logo_path;
    }
    // Before tenantConfig loads, use the bootstrapped env config
    const envWl = (window as any).__PLB_ENV__?.whitelabeling;
    if (envWl?.logo_path) {
      console.log('[Sidebar] logo from __PLB_ENV__:', envWl.logo_path);
      return envWl.logo_path;
    }
    console.log('[Sidebar] logo: default (Pliable)');
    return theme === 'dark' ? logoLargeWhite : logoLargeBlack;
  }, [tcUpdatedAt, tenantConfig, theme]);

  const isItemActive = (item: MenuItem) => {
    if (item.path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  };

  const renderMenuItem = (item: MenuItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedSections.has(item.path);
    const isActive = isItemActive(item);
    if (hasChildren) {
      return (
        <SidebarMenuItem key={item.path}>
          <SidebarMenuButton
            onClick={() => onToggleExpand(item.path)}
            isActive={isActive}
            tooltip={item.label}
          >
            {item.icon}
            <span>{item.label}</span>
            <ChevronRight
              className={`ml-auto h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            />
          </SidebarMenuButton>
          {isExpanded && (
            <SidebarMenuSub>
              {item.children!.map((child) => {
                const childIsActive = location.pathname === child.path || location.pathname.startsWith(child.path + '/');
                return (
                  <SidebarMenuSubItem key={child.path}>
                    <SidebarMenuSubButton asChild isActive={childIsActive}>
                      <Link to={child.path}>{child.label}</Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
            </SidebarMenuSub>
          )}
        </SidebarMenuItem>
      );
    }

    // If item has onClick, render as a button instead of a link
    if (item.onClick) {
      return (
        <SidebarMenuItem key={item.path}>
          <SidebarMenuButton onClick={item.onClick} tooltip={item.label}>
            {item.icon}
            <span>{item.label}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }

    return (
      <SidebarMenuItem key={item.path}>
        <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
          <Link to={item.path}>
            {item.icon}
            <span>{item.label}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <Link to="/" className="flex items-center">
          <img src={logoToUse} alt="Pliable" className="h-8" />
        </Link>
        <div data-testid="tenant-picker">
          <TenantSwitcher />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Top Section: Home and Ellie */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {topItems.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="mx-4 border-t border-border" />

        {/* Main Section: Agents, Analytics, Glossary, Data Sources, Context */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Develop/Production Mode Section */}
        <RequireRole role="Builder">
          <div className="mx-4 border-t border-border" />
          <SidebarGroup>
            <SidebarGroupContent>
              <div className="px-2 py-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="w-full text-left p-2 rounded-lg hover:bg-sidebar-accent focus:outline-none focus:ring-2 focus:ring-sidebar-ring transition-colors">
                      <div className="flex items-center gap-2">
                        {isInDraftMode ? (
                          <Wrench className="h-4 w-4 text-sidebar-foreground/80" />
                        ) : (
                          <Code className="h-4 w-4 text-sidebar-foreground/80" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-sidebar-foreground">
                            {isInDraftMode ? 'Develop Mode' : 'Production Mode'}
                          </div>
                          <div className="text-xs text-sidebar-foreground/60">
                            {isInDraftMode ? 'Making changes' : 'Read only'}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-sidebar-foreground/60" />
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="right" align="start" className="w-56 p-2">
                    <div className="space-y-3">
                      <div className="space-y-1">
                        {isInDraftMode ? (
                          <Button
                            variant="ghost"
                            className="w-full justify-start"
                            onClick={handleToggleDevelopMode}
                          >
                            <Code className="h-4 w-4 mr-2" />
                            Enter Production Mode
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            className="w-full justify-start"
                            onClick={handleToggleDevelopMode}
                          >
                            <Wrench className="h-4 w-4 mr-2" />
                            Enter Develop Mode
                          </Button>
                        )}
                      </div>
                      {isInDraftMode && (
                        <div className="border-t pt-2 space-y-1">
                          <Button
                            variant="ghost"
                            className="w-full justify-start"
                            onClick={handlePublishChanges}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Publish Changes
                          </Button>
                          <Button
                            variant="ghost"
                            className="w-full justify-start"
                            onClick={handleRevertChanges}
                            disabled={reverting}
                          >
                            <Undo2 className="h-4 w-4 mr-2" />
                            {reverting ? 'Reverting...' : 'Revert Changes'}
                          </Button>
                        </div>
                      )}
                      <div className="border-t pt-2 space-y-1">
                        <Button
                          variant="ghost"
                          className="w-full justify-start"
                          onClick={handleBuildAll}
                        >
                          <Hammer className="h-4 w-4 mr-2" />
                          Build All
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Develop mode menu items */}
              {isInDraftMode && (
                <SidebarMenu>
                  {developItems.map(renderMenuItem)}
                </SidebarMenu>
              )}
            </SidebarGroupContent>
          </SidebarGroup>
        </RequireRole>

        {/* Spacer to push bottom items down */}
        <div className="flex-1" />

        {/* Bottom Section: Settings, User Management */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {bottomItems.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center w-full p-2 rounded-lg hover:bg-sidebar-accent focus:outline-none focus:ring-2 focus:ring-sidebar-ring transition-colors">
              <Avatar.Root className="inline-flex items-center justify-center align-middle overflow-hidden select-none w-10 h-10 rounded-full bg-sidebar-accent">
                <Avatar.Image
                  className="w-full h-full object-cover"
                  src={userAvatar}
                  alt={userDisplayName}
                />
                <Avatar.Fallback className="w-full h-full flex items-center justify-center bg-sidebar-accent text-sidebar-foreground text-sm font-medium">
                  {userDisplayName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </Avatar.Fallback>
              </Avatar.Root>
              <div className="ml-3 text-left flex-1 min-w-0">
                <div className="text-sm font-medium text-sidebar-foreground truncate">
                  {userDisplayName}
                </div>
                <div className="text-xs text-sidebar-foreground/70 truncate">
                  {userEmail}
                </div>
              </div>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="min-w-[220px]"
            sideOffset={5}
            align="end"
          >
            <DropdownMenuItem
              onSelect={toggleTheme}
              className="flex items-center"
            >
              {theme === 'dark' ? (
                <Sun className="mr-2 h-4 w-4" />
              ) : (
                <Moon className="mr-2 h-4 w-4" />
              )}
              <span>Toggle {theme === 'dark' ? 'light' : 'dark'} mode</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={showAdminPortal}>
              Manage Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={() => setLoggingOut(true)}
            >
              {loggingOut ? 'Logging out...' : 'Log Out'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Hidden iframe to force SSO IdP logout before Frontegg logout */}
        {loggingOut && (
          authService.ssoLogoutUrl ? (
            <iframe
              src={authService.ssoLogoutUrl}
              style={{ width: 0, height: 0, border: 'none', position: 'absolute' }}
              onLoad={finishLogout}
              onError={finishLogout}
            />
          ) : (
            // No SSO — finish immediately (useEffect to avoid setState during render)
            <ImmediateEffect fn={finishLogout} />
          )
        )}
      </SidebarFooter>

      <BuildAllModal open={buildAllOpen} onOpenChange={setBuildAllOpen} />
      <PublishChangesDialog open={publishOpen} onOpenChange={setPublishOpen} />
    </Sidebar>
  );
}
